import * as React from 'react';
import { Button } from './button';
import { rem } from '../../lib/rem';

/**
 * Kit ErrorBoundary — a standard React class-component boundary (no new
 * dependency; this IS the library-supported mechanism, not a hand-built
 * substitute). React has no hooks equivalent — a boundary must be a class.
 *
 * Why this exists: before this component, NOTHING in src/renderer caught a
 * render throw, so one bad row (a module with a malformed `tags` value, say)
 * unmounted the ENTIRE app — the open document, every panel, all of it.
 * Wrap anything that renders data this app didn't fully control writing
 * (a toolbox panel, a page outlet) so a throw there shows a contained error
 * state instead of blanking the screen.
 *
 * `resetKey` — when it changes (e.g. the active page/panel id), the
 * boundary resets itself even if the user never clicks "Try again": a
 * crashed panel shouldn't stay crashed forever once you've navigated away
 * and back to something that might render fine now.
 */
export interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** What broke, in plain words — shown above the generic explanation. */
  title?: string;
  /** Changing this value (e.g. a page or panel id) resets the boundary. */
  resetKey?: string | number;
  /** Logged via console.error alongside the default message — this never
   *  replaces that log, only adds context (which panel, which page). */
  onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', this.props.title || '(untitled)', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <div
          role="alert"
          style={{
            display: 'grid',
            gap: 'var(--space-3)',
            padding: 'var(--space-5)',
            textAlign: 'center',
            justifyItems: 'center',
            color: 'var(--tx)',
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 'var(--font-size-md)' }}>
            {this.props.title || 'Something went wrong'}
          </div>
          <p
            style={{
              color: 'var(--tm)',
              fontSize: 'var(--font-size-base)',
              margin: 0,
              maxWidth: rem(360),
            }}
          >
            This part of the app hit an error and stopped — the rest of SpecForma is unaffected.
          </p>
          <Button variant="outline" size="sm" onClick={this.reset}>
            Try again
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
